import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Layout,
    Row,
    Col,
    Form,
    Input,
    Button,
    Upload,
    Divider,
    Alert,
    Typography,
    Card,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import LogoImage from "../assets/images/logo.svg";
import useRequest from "../hooks/useRequest";
import { ShowToast, Severty } from "../helper/toast";

const { Title } = Typography;
const { Dragger } = Upload;

// Max file size in bytes (5 MB)
const MAX_FILE_SIZE = 5 * 1024 * 1024;

const SignUp = () => {
    const { request } = useRequest();
    const navigate = useNavigate();
    const location = useLocation();
    // Single KYC endpoint; documentType will differentiate the file types
    const KYC_ENDPOINT = "/vendor/kyc";
    const [loading, setLoading] = useState(false);
    const [registeredEmail, setRegisteredEmail] = useState(null);
    const [kycStatus, setKycStatus] = useState(null); // pending / approved / rejected
    const [registeredToken, setRegisteredToken] = useState(null);

    // If navigated here with state (e.g., after KYC rejection on sign-in), use it to pre-fill
    useEffect(() => {
        const st = location?.state;
        if (st) {
            if (st.email) setRegisteredEmail(st.email);
            if (st.kycstatus) setKycStatus(st.kycstatus.toLowerCase());
            if (st.token) {
                setRegisteredToken(st.token);
                localStorage.setItem("token", st.token);
            }
            // Persist email so other flows can reuse it
            if (st.email) localStorage.setItem("email", st.email);
        }
    }, [location?.state]);

    const [civilIdFile, setCivilIdFile] = useState(null);
    const [addressProofFile, setAddressProofFile] = useState(null);
    const [identityDocsFile, setIdentityDocsFile] = useState(null);

    const validatePhoneRule = () => ({
        validator(_, value) {
            if (!value) return Promise.reject(new Error("Please input your phone number!"));
            const re = /^\+?[0-9]{7,15}$/;
            if (re.test(value)) return Promise.resolve();
            return Promise.reject(new Error("Please enter a valid phone number (7-15 digits, optional +)"));
        },
    });

    const passwordRules = [
        { required: true, message: "Please input your password!" },
        { min: 8, message: "Password must be at least 8 characters" },
        {
            pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[a-z]).+$/,
            message: "Password must contain at least one uppercase letter, one number, and one lowercase letter",
        },
    ];

    const beforeFileAdd = (file) => {
        if (file.size > MAX_FILE_SIZE) {
            ShowToast("File must be 5MB or smaller", Severty.ERROR);
            return Upload.LIST_IGNORE || false;
        }
        return false; // prevent auto upload; we'll manage files in state
    };

    const handleRegister = async (values) => {
        const { name, email, phone, password, confirm_password } = values;
        setLoading(true);
        try {
            await request({
                url: "/vendor/register",
                method: "POST",
                data: { name, email, phone, password, confirmPassword: confirm_password },
                onSuccess: (data) => {
                    setLoading(false);
                    
                    if (data.status) {
                        setRegisteredEmail(email);
                        setRegisteredToken(data.data?.token || null);
                        localStorage.setItem("token", data.data?.token || "");
                        setKycStatus(data.data?.kycStatus || "pending");
                        ShowToast(data.message || "Registered successfully. Please upload KYC documents.", Severty.SUCCESS);
                    } else {
                        ShowToast(data.message || "Registration failed", Severty.ERROR);
                    }
                },
                onError: (err) => {
                    setLoading(false);
                    const apiError  = err?.response?.data?.data?.error?.[0] || err?.response?.data?.message;
                    ShowToast(apiError || "Registration error", Severty.ERROR);
                },
            });
        } catch (e) {
            setLoading(false);
        }
    };

    const handleKycSubmit = async () => {
        if (!registeredEmail) {
            ShowToast("Please register first", Severty.ERROR);
            return;
        }

        if (!civilIdFile || !addressProofFile || !identityDocsFile) {
            ShowToast("Please upload all required KYC documents", Severty.ERROR);
            return;
        }
        setLoading(true);
        try {
            // helper to upload a single file to /vendor/kyc with documentType and 'document' field
            const uploadFile = async (documentType, file) => {
                const fd = new FormData();
                fd.append("email", registeredEmail);
                fd.append("documentType", documentType);
                fd.append("file", file);
                const res = await request({
                    url: KYC_ENDPOINT,
                    method: "POST",
                    data: fd,
                    header: {
                        "Content-Type": "multipart/form-data",
                    },
                    onSuccess: (data) => {
                        return data;
                    },
                    onError: (err) => {
                        ShowToast(err?.response?.data?.message || `${documentType} upload error`, Severty.ERROR);

                    },
                });
                
                console.log(res, ">>>>> res");
                return res;
            };

            // Upload civil id
            const resCivil = await  uploadFile("civil_id", civilIdFile)
          
            const resAddress = await uploadFile("address_proof", addressProofFile);
           
            const resIdentity = await uploadFile("identity_document", identityDocsFile);

            // If all succeeded, update status from last response if provided
            setKycStatus((resIdentity && resIdentity.data && resIdentity.data.kycStatus) || "pending");
            if (resCivil.statusText == "SUCCESS" && resAddress.statusText == "SUCCESS" && resIdentity.statusText == "SUCCESS") {
            ShowToast("KYC uploaded successfully", Severty.SUCCESS);
            // remove token after successful KYC
            localStorage.removeItem("token");
            navigate('/login');
            }

            setLoading(false);

        } catch (e) {
            setLoading(false);
            ShowToast("KYC upload error", Severty.ERROR);
        }
    };

    const fileProps = (setter) => ({
        name: "file",
        multiple: false,
        beforeUpload: (file) => {
            const ok = beforeFileAdd(file);
            if (ok === false) {
                // user-provided file is accepted by beforeFileAdd returning false, but we still need to store it
                setter(file);
            } else if (ok === Upload.LIST_IGNORE) {
                // ignored
            } else {
                // other behavior
            }
            return false;
        },
        onRemove: () => {
            setter(null);
        },
        accept: ".png,.jpg,.jpeg,.pdf",
    });

    return (
        <>
            <Layout className="layout-default layout-signin-bg"></Layout>
            <Layout className="layout-default layout-signin">
                <Row justify="center" align="middle" style={{ minHeight: "100vh" }}>
                    <Col xs={22} sm={18} md={12} lg={8}>
                        <Card bordered={false} style={{ borderRadius: 12, padding: 24 }}>
                            <div style={{ textAlign: "center", marginBottom: 16 }}>
                                <img src={LogoImage} alt="logo" style={{ width: 120 }} />
                                <Title level={3}>Vendor Sign Up</Title>
                                <div >Create an account and submit KYC to get approved</div>
                            </div>

                            {!registeredEmail && (
                                <Form layout="vertical" onFinish={handleRegister}>
                                    <Form.Item label="Name" name="name" normalize={(value) => value?.trim()} rules={[{ required: true, message: "Please enter your name" }, { max: 50, message: "Name cannot exceed 50 characters" }]}>
                                        <Input placeholder="Full name" />
                                    </Form.Item>

                                    <Form.Item label="Email" name="email" rules={[{ required: true, type: "email", message: "Please enter a valid email" }, { max: 100 }]}>
                                        <Input placeholder="Email" />
                                    </Form.Item>

                                    <Form.Item label="Phone" name="phone" rules={[{ required: true }, validatePhoneRule()]}>
                                        <Input placeholder="Phone (e.g. +1234567890)" />
                                    </Form.Item>

                                    <Form.Item label="Password" name="password" rules={passwordRules}>
                                        <Input.Password placeholder="Password" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Confirm Password"
                                        name="confirm_password"
                                        dependencies={["password"]}
                                        rules={[
                                            { required: true, message: "Please confirm your password" },
                                            ({ getFieldValue }) => ({
                                                validator(_, value) {
                                                    if (!value || getFieldValue("password") === value) return Promise.resolve();
                                                    return Promise.reject(new Error("Passwords do not match"));
                                                },
                                            }),
                                        ]}
                                    >
                                        <Input.Password placeholder="Confirm Password" />
                                    </Form.Item>

                                    <Form.Item>
                                        <Button type="primary" htmlType="submit" block loading={loading}>
                                            Submit
                                        </Button>
                                    </Form.Item>
                                </Form>
                            )}

                            {registeredEmail && (
                                <div>
                                    <Alert
                                        message={`KYC Status: ${kycStatus ? kycStatus.toUpperCase() : "PENDING"}`}
                                        type={kycStatus === "approved" ? "success" : kycStatus === "rejected" ? "error" : "info"}
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                    />

                                    {kycStatus === "rejected" && (
                                        <Alert
                                            message= {`Your KYC was rejected. Please review the guidance and re-submit the required documents.${location?.state?.reason ? " Reason: " + location.state.reason : ""}`}   
                                            type="warning"
                                            showIcon
                                            style={{ marginBottom: 16 }}
                                        />
                                    )}

                                    <Divider>Upload KYC Documents</Divider>

                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ marginBottom: 6 }}>Civil ID (PNG/JPG/PDF, ≤5MB)</div>
                                        <Dragger {...fileProps(setCivilIdFile)}>
                                            <p className="ant-upload-drag-icon">
                                                <InboxOutlined />
                                            </p>
                                            <p className="ant-upload-text">Click or drag file to this area to upload</p>
                                            <p className="ant-upload-hint">Only PNG/JPG/PDF allowed. Max 5MB.</p>
                                            {civilIdFile && <div style={{ marginTop: 8 }}>{civilIdFile.name}</div>}
                                        </Dragger>
                                    </div>

                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ marginBottom: 6 }}>Address Proof (PNG/JPG/PDF, ≤5MB)</div>
                                        <Dragger {...fileProps(setAddressProofFile)}>
                                            <p className="ant-upload-drag-icon">
                                                <InboxOutlined />
                                            </p>
                                            <p className="ant-upload-text">Click or drag file to this area to upload</p>
                                            <p className="ant-upload-hint">Only PNG/JPG/PDF allowed. Max 5MB.</p>
                                            {addressProofFile && <div style={{ marginTop: 8 }}>{addressProofFile.name}</div>}
                                        </Dragger>
                                    </div>

                                    <div style={{ marginBottom: 12 }}>
                                        <div style={{ marginBottom: 6 }}>Identity Documents (PNG/JPG/PDF, ≤5MB)</div>
                                        <Dragger {...fileProps(setIdentityDocsFile)}>
                                            <p className="ant-upload-drag-icon">
                                                <InboxOutlined />
                                            </p>
                                            <p className="ant-upload-text">Click or drag file to this area to upload</p>
                                            <p className="ant-upload-hint">Only PNG/JPG/PDF allowed. Max 5MB.</p>
                                            {identityDocsFile && <div style={{ marginTop: 8 }}>{identityDocsFile.name}</div>}
                                        </Dragger>
                                    </div>

                                    <Button type="primary" block onClick={handleKycSubmit} loading={loading}>
                                        Submit KYC
                                    </Button>

                                    <div style={{ marginTop: 12 }}>
                                        <Button type="link" onClick={() => { setRegisteredEmail(null); setKycStatus(null); }}>
                                            Start Over
                                        </Button>
                                        <Button type="link" onClick={() => navigate('/vendor/login')}>Go to Login</Button>
                                    </div>
                                </div>
                            )}
                        </Card>
                    </Col>
                </Row>
            </Layout>
        </>
    );
};

export default SignUp;
